import { Close } from '@fountain/fountain-ui-components';
import {
  Button,
  FormHelperText,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import clsx from 'clsx';
import React from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';

import { PlusIcon } from 'images/PlusIcon';

import messages from './messages';
import {
  backupApproverCount,
  BackupDraft,
  emptyBackup,
  ESCALATE_AFTER_MAX,
  ESCALATE_AFTER_MIN,
  escalateUnitOptions,
} from './ruleForm';
import {
  SearchableMultiSelect,
  SearchableMultiSelectOption,
} from './SearchableMultiSelect';
import { useStyles } from './styles';

/**
 * How each unit token reads.
 *
 * Keyed by the stored token rather than by the two the picker offers, because
 * `escalateUnitOptions` will list a third when a level already carries one — see
 * `escalateUnitBusinessDays`. A token with no entry falls back to itself, which
 * is the legible degradation `ConditionSelect` settles on for the same case: an
 * option nobody can read still beats an option nobody can pick.
 */
const UNIT_MESSAGES: Record<string, MessageDescriptor> = {
  hours: messages.escalateUnitHours,
  days: messages.escalateUnitDays,
  business_days: messages.escalateUnitBusinessDays,
};

export interface BackupApproversProps {
  /** `null` renders the affordance that adds a block, not the block. */
  backup: BackupDraft | null;
  /**
   * The account's people, already mapped and memoised by the panel.
   *
   * One array for every roster on the panel rather than one built here per
   * level: react-select takes a `setState` whenever `options` changes by
   * identity, and this component re-renders on every keystroke in the wait
   * field beside it.
   */
  approverOptions: SearchableMultiSelectOption[];
  /** This level's prefix, so its controls stay addressable per card. */
  idPrefix: string;
  /**
   * Which wait message is due, once a save has been refused.
   *
   * Two, not a boolean: an empty field and a field holding a fraction are
   * different mistakes, and one sentence cannot be true of both — see
   * `escalateAfterInvalid`.
   */
  timerError?: 'missing' | 'invalid' | 'unit';
  onChange: (backup: BackupDraft | null) => void;
}

/**
 * One level's backup approvers and the wait that brings them in (AXHE-3932).
 *
 * ## What the design asks for, and what is built
 *
 * Design Requirements §3: each level offers "Add backup approvers", and once
 * added shows a block with "If no response after [N] [Hours | Days]", the same
 * approver row structure the primaries use, an Any / All control when there is
 * more than one, and a way to remove the whole block. Three of those four are
 * here. Two deliberate departures, both of which are the same rule applied
 * twice — do not ship a control that changes nothing:
 *
 * - **No Any / All control.** How many backups act is not a question the rule
 *   asks. A level needs as many distinct sign-offs as it has primaries, and a
 *   primary or an escalated backup supplies one alike —
 *   `Approvals::Step#approval_satisfied?` is the authority. No reading of a
 *   backup Any / All changes an outcome, so the control is not drawn and the
 *   column behind it is gone.
 * - **A roster, not typed rows.** §3 asks for "the same Name/User-attribute row
 *   structure" as the primaries. Role and user-attribute approvers are out of
 *   the MVP, so a typed row would carry one type — and the primaries are one
 *   searchable roster of named people. This is that same structure, which is
 *   what §3 asks for; the typed row is what neither roster has.
 *
 * ## Why the remove control can go away
 *
 * A role backup has no `user_id`, so it cannot be a selection in the roster and
 * is carried on the draft instead. Removing the block sends no `backup_approvers`
 * at all, and `SaveRule` rebuilds the level from what it is sent — so the remove
 * would silently delete an approver the panel never showed. The same reason the
 * scope withholds its "All …" choice: a panel that cannot render a row must not
 * offer to delete it. Withheld rather than hidden, with the count said out loud,
 * so the state is legible instead of the control being mysteriously absent.
 */
export const BackupApprovers: React.FC<BackupApproversProps> = ({
  backup,
  approverOptions,
  idPrefix,
  timerError,
  onChange,
}) => {
  const styles = useStyles();
  const intl = useIntl();

  if (!backup) {
    return (
      <div className={styles.backupSection}>
        <Button
          className={styles.addLink}
          startIcon={<PlusIcon color="inherit" />}
          onClick={() => onChange(emptyBackup())}
        >
          <FormattedMessage {...messages.addBackupApprovers} />
        </Button>
      </div>
    );
  }

  const hiddenCount = backup.roleApprovers.length;
  // Nobody at all, visible or carried. The block still renders — see
  // `backupDraftFor` for why a wait with an empty roster is opened rather than
  // left buried — and says what it does instead of looking configured.
  const isUnstaffed = backupApproverCount(backup) === 0;
  const timerId = `${idPrefix}-escalate-after`;
  const timerErrorId = `${timerId}-helper-text`;
  // Whichever message is due belongs to one of the two controls, never both: the
  // amount owns "missing" and "invalid", the unit owns its own. Marking the
  // amount for a missing unit paints a field the admin cannot fix — and the
  // helper text below, which is the thing that says what to do, is already
  // pointed at the select.
  const amountError = timerError !== undefined && timerError !== 'unit';

  return (
    <div className={styles.backupSection}>
      <div className={styles.backupRosterRow}>
        {/* The same control the level's own roster takes, and the same one the
            condition rows take: an account names hundreds of people, and a
            roster that can only be scrolled is unusable at that size. One
            control for both rosters is the point — a card asking who signs off
            twice must not ask it in two shapes. */}
        <SearchableMultiSelect
          id={`${idPrefix}-backup-approvers`}
          className={clsx(styles.backupRoster, styles.captionedField)}
          label={intl.formatMessage(messages.backupApproversLabel)}
          values={backup.approverIds}
          options={approverOptions}
          onChange={approverIds => onChange({ ...backup, approverIds })}
        />

        {/* Destructive, so never primary weight — an icon button at the end of
            the row rather than a button beside the roster. Withheld, not hidden,
            when the block holds a row this panel cannot show. */}
        {hiddenCount === 0 && (
          <IconButton
            className={styles.backupRemove}
            size="small"
            aria-label={intl.formatMessage(messages.removeBackupApprovers)}
            onClick={() => onChange(null)}
          >
            <Close className={styles.removeIcon} />
          </IconButton>
        )}
      </div>

      {/* "If no response after [ 2 ] [ Days ]" — one message with both controls
          interpolated, so a translator can put them where the language wants them
          rather than after a fixed leading phrase. `component="div"` because the
          row is a flex container holding inputs, and a `<p>` may not contain them.

          Each control carries its own accessible name; see `escalateAfterLabel`
          for why those stand alone, and for the group framing this deliberately
          does without. */}
      <Typography
        variant="body2"
        component="div"
        className={clsx(styles.escalateTimer, styles.escalateTimerText)}
      >
        <FormattedMessage
          {...messages.escalateAfterSentence}
          values={{
            amount: (
              <TextField
                key="amount"
                id={timerId}
                className={clsx(
                  styles.outlinedField,
                  styles.escalateAfterField,
                )}
                InputProps={{ disableUnderline: true }}
                value={backup.escalateAfter}
                // Read out of the event before the updater rather than inside it.
                // The panel's name field carries the long version of why: React 16
                // pools synthetic events, and a lazy read of one it had released
                // took the whole editor down.
                onChange={event => {
                  const { value } = event.target;
                  onChange({ ...backup, escalateAfter: value });
                }}
                error={amountError}
                type="number"
                inputProps={{
                  'aria-label': intl.formatMessage(messages.escalateAfterLabel),
                  // The floor the server enforces too, and a ceiling it does not
                  // — see `ESCALATE_AFTER_MAX`. On the input rather than in
                  // `validate`, so the stepper stops at 30 without a rule the API
                  // set to 45 becoming unsavable from here.
                  min: ESCALATE_AFTER_MIN,
                  max: ESCALATE_AFTER_MAX,
                  ...(amountError && { 'aria-describedby': timerErrorId }),
                }}
              />
            ),
            // Carries no `id`, which is a departure from every other control in
            // this panel and is the load-bearing half of naming it. MUI v4 routes
            // a `TextField`'s `id` into `SelectDisplayProps.id`, then points the
            // display node's `aria-labelledby` at that same id — a self-reference,
            // which resolves the accessible name to the node's own text ("Days")
            // and outranks any `aria-label` whichever prop supplied it. With no
            // id there is no `aria-labelledby`, so the name below is the name.
            // Addressed in tests through the level card rather than by id.
            //
            // `SelectDisplayProps` and not `SelectProps`: the latter spreads onto
            // the `InputBase` wrapper, whose div is not the control and does not
            // open the menu. `inputProps` would land it on the same display node
            // this does — `SelectInput` destructures `aria-label` off its own
            // props before building the spread that reaches the hidden input — so
            // it is a matter of saying where the name goes, not of avoiding a trap.
            unit: (
              <TextField
                key="unit"
                className={clsx(styles.outlinedField, styles.escalateUnitField)}
                InputProps={{ disableUnderline: true }}
                value={backup.escalateUnit}
                onChange={event => {
                  const { value } = event.target;
                  onChange({ ...backup, escalateUnit: value });
                }}
                error={timerError === 'unit'}
                SelectProps={{
                  SelectDisplayProps: {
                    'aria-label': intl.formatMessage(messages.escalateUnitLabel),
                    ...(timerError === 'unit' && {
                      'aria-describedby': timerErrorId,
                    }),
                  },
                }}
                select
              >
                {escalateUnitOptions(backup.storedUnit).map(unit => (
                  <MenuItem key={unit} value={unit}>
                    {UNIT_MESSAGES[unit] ? (
                      <FormattedMessage {...UNIT_MESSAGES[unit]} />
                    ) : (
                      unit
                    )}
                  </MenuItem>
                ))}
              </TextField>
            ),
          }}
        />
      </Typography>

      {/* `role="alert"`, mounted only once a save has been refused, for the
          reason the scope group's message carries it: the appearance is the event
          to announce, and a refused save moves no focus. */}
      {timerError && (
        <FormHelperText id={timerErrorId} error role="alert">
          <FormattedMessage
            {...(timerError === 'unit'
              ? messages.escalateUnitRequired
              : timerError === 'invalid'
                ? messages.escalateAfterInvalid
                : messages.escalateAfterRequired)}
          />
        </FormHelperText>
      )}

      {isUnstaffed && (
        <Typography
          variant="body2"
          className={styles.inlineWarning}
          role="status"
        >
          <FormattedMessage {...messages.backupApproversMissing} />
        </Typography>
      )}

      {hiddenCount > 0 && (
        <Typography variant="body2" color="textSecondary" role="status">
          <FormattedMessage
            {...messages.backupApproversElsewhere}
            values={{ count: hiddenCount }}
          />
        </Typography>
      )}
    </div>
  );
};
